if (!customElements.get('video-review-marquee')) {
  class VideoReviewMarquee extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;
      this.isInitialized = true;
      this.rebuildFrame = null;
      this.animationFrame = null;
      this.lastFrameTime = null;
      this.isVisible = true;
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.scrollSpeed = Number.parseFloat(this.dataset.scrollSpeed) || 40;
      this.handleClickBound = this.handleClick.bind(this);
      this.handlePointerDownBound = this.handlePointerDown.bind(this);
      this.handlePointerMoveBound = this.handlePointerMove.bind(this);
      this.handlePointerEndBound = this.handlePointerEnd.bind(this);
      this.handleFocusInBound = this.handleFocusIn.bind(this);
      this.handleModalChangeBound = this.handleModalChange.bind(this);
      this.handleMutationsBound = this.handleMutations.bind(this);
      this.animateBound = this.animate.bind(this);
      this.handleVisibilityChangeBound = this.handleVisibilityChange.bind(this);
      this.dragState = null;
      this.isDragging = false;
      this.suppressClick = false;
      this.dragClickSuppressTimer = null;
      this.resumeAutoScrollRow = null;
      this.modalRestoreTarget = null;

      this.addEventListener('click', this.handleClickBound);
      this.addEventListener('pointerdown', this.handlePointerDownBound);
      this.addEventListener('pointermove', this.handlePointerMoveBound);
      this.addEventListener('pointerup', this.handlePointerEndBound);
      this.addEventListener('pointercancel', this.handlePointerEndBound);
      this.addEventListener('focusin', this.handleFocusInBound);
      document.addEventListener('visibilitychange', this.handleVisibilityChangeBound);
      document.addEventListener('pointerup', this.handlePointerEndBound);
      document.addEventListener('pointercancel', this.handlePointerEndBound);
      this.modal = document.querySelector(this.dataset.modalId);
      this.modalBody = this.modal?.querySelector('[data-video-review-modal-body]');

      if (this.modal) {
        this.modalObserver = new MutationObserver(this.handleModalChangeBound);
        this.modalObserver.observe(this.modal, {
          attributes: true,
          attributeFilter: ['open'],
        });
      }

      this.mutationObserver = new MutationObserver(this.handleMutationsBound);
      this.mutationObserver.observe(this, {
        childList: true,
        subtree: true,
      });

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.scheduleRebuild());
        this.resizeObserver.observe(this);
      }

      if (typeof IntersectionObserver !== 'undefined') {
        this.intersectionObserver = new IntersectionObserver(([entry]) => {
          this.isVisible = entry.isIntersecting;
          this.lastFrameTime = null;
        });
        this.intersectionObserver.observe(this);
      }

      this.scheduleRebuild();
      this.animationFrame = window.requestAnimationFrame(this.animateBound);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClickBound);
      this.removeEventListener('pointerdown', this.handlePointerDownBound);
      this.removeEventListener('pointermove', this.handlePointerMoveBound);
      this.removeEventListener('pointerup', this.handlePointerEndBound);
      this.removeEventListener('pointercancel', this.handlePointerEndBound);
      this.removeEventListener('focusin', this.handleFocusInBound);
      document.removeEventListener('visibilitychange', this.handleVisibilityChangeBound);
      document.removeEventListener('pointerup', this.handlePointerEndBound);
      document.removeEventListener('pointercancel', this.handlePointerEndBound);
      this.modalObserver?.disconnect();
      this.mutationObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();

      if (this.rebuildFrame) {
        window.cancelAnimationFrame(this.rebuildFrame);
      }
      if (this.animationFrame) {
        window.cancelAnimationFrame(this.animationFrame);
      }
      if (this.dragClickSuppressTimer) {
        window.clearTimeout(this.dragClickSuppressTimer);
      }

      this.cancelDrag();
      this.cleanupModal(false);
      this.modal?.remove();
      this.isInitialized = false;
    }

    handleMutations(mutations) {
      const requiresRebuild = mutations.some((mutation) => {
        if (mutation.target.closest?.('[data-marquee-clone]')) return false;

        const changedNodes = [...mutation.addedNodes, ...mutation.removedNodes];
        return changedNodes.some((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return true;
          return !node.matches('[data-marquee-clone]');
        });
      });

      if (requiresRebuild) this.scheduleRebuild();
    }

    scheduleRebuild() {
      if (this.rebuildFrame) window.cancelAnimationFrame(this.rebuildFrame);
      this.rebuildFrame = window.requestAnimationFrame(() => {
        this.rebuildFrame = null;
        this.rebuildRows();
      });
    }

    rebuildRows() {
      this.querySelectorAll('[data-video-review-row]').forEach((row) => {
        const viewport = row.querySelector('[data-video-review-viewport]');
        const track = row.querySelector('[data-video-review-track]');
        const originalGroup = track?.querySelector('[data-video-review-group]:not([data-marquee-clone])');

        if (!viewport || !track || !originalGroup) return;

        track.querySelectorAll('[data-marquee-clone]').forEach((clone) => clone.remove());
        row.marqueeOffset = 0;

        const groupWidth = originalGroup.getBoundingClientRect().width;
        const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
        const distance = groupWidth + gap;

        if (groupWidth <= 0 || viewport.clientWidth <= 0) return;

        const groupCount = Math.max(2, Math.ceil(viewport.clientWidth / distance) + 2);
        const fragment = document.createDocumentFragment();

        for (let index = 1; index < groupCount; index += 1) {
          const clone = originalGroup.cloneNode(true);
          this.prepareClone(clone);
          fragment.appendChild(clone);
        }

        track.appendChild(fragment);
        row.marqueeDistance = distance;
        this.renderRow(row);
      });
      this.lastFrameTime = null;
    }

    animate(timestamp) {
      const canAnimate =
        this.isVisible &&
        !document.hidden &&
        !this.prefersReducedMotion.matches &&
        !this.isDragging &&
        !this.classList.contains('is-modal-open');

      if (canAnimate && this.lastFrameTime !== null) {
        const elapsed = Math.min(timestamp - this.lastFrameTime, 64);

        this.querySelectorAll('[data-video-review-row]').forEach((row) => {
          const hasFocusedElement = row.contains(document.activeElement);
          const shouldKeepScrollingAfterModalClose = this.resumeAutoScrollRow === row;

          if (
            !row.marqueeDistance ||
            row.matches(':hover') ||
            (hasFocusedElement && !shouldKeepScrollingAfterModalClose)
          ) {
            return;
          }

          row.marqueeOffset =
            (row.marqueeOffset + (this.scrollSpeed * elapsed) / 1000) % row.marqueeDistance;
          this.renderRow(row);
        });
      }

      this.lastFrameTime = canAnimate ? timestamp : null;
      this.animationFrame = window.requestAnimationFrame(this.animateBound);
    }

    renderRow(row) {
      const track = row.querySelector('[data-video-review-track]');
      if (!track || !row.marqueeDistance) return;

      const offset =
        row.dataset.direction === 'right'
          ? row.marqueeOffset - row.marqueeDistance
          : -row.marqueeOffset;

      track.style.transform = `translate3d(${offset}px, 0, 0)`;
    }

    handleVisibilityChange() {
      this.lastFrameTime = null;
    }

    handlePointerDown(event) {
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;

      const viewport = event.target.closest('[data-video-review-viewport]');
      const row = viewport?.closest('[data-video-review-row]');
      if (!viewport || !row || !this.contains(viewport) || !row.marqueeDistance) return;

      this.cancelDrag();
      this.dragState = {
        row,
        viewport,
        pointerId: event.pointerId,
        startX: event.clientX,
        startOffset: row.marqueeOffset || 0,
        didDrag: false,
      };
      this.isDragging = true;
      row.classList.add('is-dragging');
      this.lastFrameTime = null;
    }

    handlePointerMove(event) {
      const dragState = this.dragState;
      if (!dragState || dragState.pointerId !== event.pointerId) return;

      const distanceX = event.clientX - dragState.startX;
      if (!dragState.didDrag && Math.abs(distanceX) < 6) return;

      dragState.didDrag = true;
      if (!dragState.viewport.hasPointerCapture?.(event.pointerId)) {
        dragState.viewport.setPointerCapture?.(event.pointerId);
      }
      const directionMultiplier = dragState.row.dataset.direction === 'right' ? 1 : -1;
      dragState.row.marqueeOffset = this.normalizeOffset(
        dragState.startOffset + distanceX * directionMultiplier,
        dragState.row.marqueeDistance,
      );
      this.renderRow(dragState.row);

      if (event.cancelable) event.preventDefault();
    }

    handlePointerEnd(event) {
      const dragState = this.dragState;
      if (!dragState || dragState.pointerId !== event.pointerId) return;

      if (dragState.viewport.hasPointerCapture?.(event.pointerId)) {
        dragState.viewport.releasePointerCapture(event.pointerId);
      }
      this.finishDrag(dragState.didDrag);
    }

    finishDrag(suppressClick) {
      const dragState = this.dragState;
      if (!dragState) return;

      dragState.row.classList.remove('is-dragging');
      this.dragState = null;
      this.isDragging = false;
      this.lastFrameTime = null;

      if (!suppressClick) return;

      this.suppressClick = true;
      if (this.dragClickSuppressTimer) window.clearTimeout(this.dragClickSuppressTimer);
      this.dragClickSuppressTimer = window.setTimeout(() => {
        this.suppressClick = false;
        this.dragClickSuppressTimer = null;
      }, 400);
    }

    cancelDrag() {
      if (!this.dragState) return;

      const { row, viewport, pointerId } = this.dragState;
      if (viewport.hasPointerCapture?.(pointerId)) viewport.releasePointerCapture(pointerId);
      row.classList.remove('is-dragging');
      this.dragState = null;
      this.isDragging = false;
    }

    normalizeOffset(offset, distance) {
      return ((offset % distance) + distance) % distance;
    }

    handleFocusIn(event) {
      const row = event.target.closest?.('[data-video-review-row]');
      if (row && this.resumeAutoScrollRow === row && event.target !== this.modalRestoreTarget) {
        this.resumeAutoScrollRow = null;
        this.modalRestoreTarget = null;
      }
    }

    prepareClone(clone) {
      clone.dataset.marqueeClone = '';
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((element) => {
        element.removeAttribute('data-shopify-editor-block');
      });
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
    }

    handleClick(event) {
      if (this.suppressClick) {
        event.preventDefault();
        this.suppressClick = false;
        return;
      }

      const trigger = event.target.closest('[data-video-review-trigger]');
      if (!trigger || !this.contains(trigger) || trigger.disabled) return;

      const template = trigger.querySelector('[data-video-review-template]');
      if (!template || !this.modal || !this.modalBody || typeof this.modal.show !== 'function') return;

      this.modalBody.replaceChildren(template.content.cloneNode(true));
      this.classList.add('is-modal-open');
      this.resumeAutoScrollRow = null;
      this.modalRestoreTarget = null;
      this.activeTrigger = trigger;
      this.modal.show(trigger);

      const video = this.modalBody.querySelector('video');
      video?.play().catch(() => {});

      window.requestAnimationFrame(() => {
        this.modal.querySelector('[role="dialog"]')?.focus();
      });
    }

    handleModalChange() {
      if (!this.modal?.hasAttribute('open')) {
        this.cleanupModal(true);
        this.lastFrameTime = null;
      }
    }

    cleanupModal(restoreFocus) {
      const restoreTarget = restoreFocus ? this.getRestoreTarget() : null;

      this.classList.remove('is-modal-open');
      this.modalBody?.querySelectorAll('video').forEach((video) => video.pause());
      this.modalBody?.replaceChildren();

      if (restoreTarget) {
        // 弹窗关闭后允许焦点所在行继续自动滚动。
        this.resumeAutoScrollRow = restoreTarget.closest('[data-video-review-row]');
        this.modalRestoreTarget = restoreTarget;
        restoreTarget.focus();
      } else {
        this.resumeAutoScrollRow = null;
        this.modalRestoreTarget = null;
      }

      this.activeTrigger = null;
    }

    getRestoreTarget() {
      const trigger = this.activeTrigger || this.modal?.openedBy;
      const clone = trigger?.closest('[data-marquee-clone]');
      if (!clone) return trigger;

      const cardKey = trigger.closest('[data-video-review-card]')?.dataset.videoReviewCardKey;
      if (!cardKey) return null;

      return Array.from(
        this.querySelectorAll(
          '[data-video-review-group]:not([data-marquee-clone]) [data-video-review-card]',
        ),
      )
        .find((card) => card.dataset.videoReviewCardKey === cardKey)
        ?.querySelector('[data-video-review-trigger]');
    }
  }

  customElements.define('video-review-marquee', VideoReviewMarquee);
}
